/**
 * Typed client for the file-drop inbox API (/api/uploads).
 *
 * A human drops a file; it lands in the dev-server inbox. They then tell the AI in chat
 * and the AI moves the file to the right place (move action is AI-driven; a move call is
 * exposed here too in case a UI wants it).
 *
 * WHY THIS SPLITS BIG FILES: the cockpit is reached through an nginx proxy whose default
 * `client_max_body_size` is 1 MB. A single-request upload of anything larger is refused by
 * the proxy with a 413 that never reaches FastAPI, so the page could only report a bare
 * "Upload failed. Please try again." with no way to act on it. Anything sizeable is
 * therefore posted as a series of sub-megabyte parts to /api/uploads/chunk, which the
 * backend reassembles into exactly the same inbox entry. A one-shot upload that gets a 413
 * anyway (a stricter proxy in front of us) automatically retries the chunked way, so this
 * keeps working regardless of what the deployment's body limit happens to be.
 */
import axios from 'axios';
import { attachAuth } from './auth';

const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return '/api';
};

const api = axios.create({ baseURL: getApiBaseUrl() });

// /api/uploads is gated like every other route.
attachAuth(api);

/** Send in one request only while comfortably under the 1 MB proxy body cap. */
export const SINGLE_SHOT_LIMIT = 700 * 1024;
/** Part size for a chunked upload. Also has to clear the same cap, with headroom. */
export const CHUNK_SIZE = 512 * 1024;

export interface UploadMetadata {
  id: string;
  original_filename: string | null;
  stored_filename: string;
  stored_path: string;
  size: number;
  uploaded_by: string;
  note: string | null;
  uploaded_at: string;
  moved_to: string | null;
  moved_at: string | null;
}

interface ChunkAck {
  status: 'received' | 'complete';
  received: number;
  total: number;
  upload: UploadMetadata | null;
}

/** Fraction complete, 0..1. Called as parts land so the page can show progress. */
export type UploadProgress = (fraction: number) => void;

const newSessionId = (): string => {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // The id only has to be unique per in-flight upload; the server validates its shape.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.padEnd(16, '0');
};

const uploadWhole = async (
  file: File,
  uploadedBy: string,
  note?: string
): Promise<UploadMetadata> => {
  const form = new FormData();
  form.append('file', file);
  form.append('uploaded_by', uploadedBy);
  if (note) form.append('note', note);
  const res = await api.post('/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

const uploadInChunks = async (
  file: File,
  uploadedBy: string,
  note: string | undefined,
  onProgress?: UploadProgress
): Promise<UploadMetadata> => {
  const sessionId = newSessionId();
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

  for (let index = 0; index < totalChunks; index += 1) {
    const slice = file.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE);
    const form = new FormData();
    form.append('chunk', slice, `part-${index}`);
    form.append('upload_session_id', sessionId);
    form.append('chunk_index', String(index));
    form.append('total_chunks', String(totalChunks));
    form.append('filename', file.name);
    form.append('uploaded_by', uploadedBy);
    if (note) form.append('note', note);

    // Sequential on purpose: it keeps the server's part accounting simple and means a
    // slow connection never has several megabytes in flight at once.
    const res = await api.post<ChunkAck>('/uploads/chunk', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (onProgress) onProgress((index + 1) / totalChunks);

    if (res.data.status === 'complete') {
      if (!res.data.upload) throw new Error('The server finished the upload but returned no file.');
      return res.data.upload;
    }
  }

  throw new Error('The upload ended with parts still missing. Please try again.');
};

export const uploadFile = async (
  file: File,
  uploadedBy: string,
  note?: string,
  onProgress?: UploadProgress
): Promise<UploadMetadata> => {
  if (file.size <= SINGLE_SHOT_LIMIT) {
    try {
      const entry = await uploadWhole(file, uploadedBy, note);
      if (onProgress) onProgress(1);
      return entry;
    } catch (e: any) {
      // A proxy stricter than we expect. Fall back rather than fail the human.
      if (e?.response?.status !== 413) throw e;
    }
  }
  return uploadInChunks(file, uploadedBy, note, onProgress);
};

/**
 * Turn an upload failure into something a human can act on.
 *
 * The old page read `e.response.data.detail` and fell back to "Upload failed. Please try
 * again.", which is exactly what a proxy 413 produces: the body is nginx's HTML error
 * page, so there is no `detail` to read and the real cause (too large) never reached the
 * screen. Always say what actually happened.
 */
export const describeUploadError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;

  const status = e?.response?.status;
  if (status === 413) {
    return 'The server rejected the file as too large. Please tell an admin the upload proxy needs a bigger body limit.';
  }
  if (status === 401) return 'Your session expired. Sign in again and retry the upload.';
  if (status === 403) return 'You are not allowed to upload here.';
  if (!e?.response) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (typeof e?.message === 'string' && e.message && !e.message.startsWith('Request failed')) {
    return e.message;
  }
  return `Upload failed (HTTP ${status}). Please try again.`;
};

export const listUploads = async (limit: number = 100): Promise<UploadMetadata[]> => {
  const res = await api.get('/uploads', { params: { limit } });
  return res.data;
};

export const moveUpload = async (
  uploadId: string,
  destPath: string
): Promise<UploadMetadata> => {
  const res = await api.post(`/uploads/${uploadId}/move`, { dest_path: destPath });
  return res.data;
};
